import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (!this.hasAttribute('auto-open')) return;

    // De header staat twee keer in de pagina (desktop en mobiel), dus ook de lade.
    // Alleen de zichtbare openen: een onzichtbare open modal blokkeert alle klikken.
    const drawers = [...document.querySelectorAll('cart-drawer-component[auto-open]')];
    const target = drawers.find((drawer) => drawer.getClientRects().length > 0) ?? drawers[0];
    if (target === this) this.showDialog();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

/*
 * Upsell in de lade (14-10-2026): "Toevoegen" zonder de lade te verlaten.
 * Eén listener op document (de lade staat 2x in de pagina en wordt na elke wijziging vervangen).
 * Zelfde route en event als sn-pdp.js: CartAddEvent met de sectie-HTML, zodat de lade, de verzendmeter en het mandje verversen.
 */
if (!window.snCartUpsell) {
  window.snCartUpsell = true;
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-sn-upsell')) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');

    const formData = new FormData(form);
    const sectionIds = [...new Set(
      [...document.querySelectorAll('cart-items-component')]
        .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
        .filter(Boolean)
    )];
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json();
      if (data.status) return;
      form.dispatchEvent(
        new CartAddEvent({}, String(formData.get('id')), {
          source: 'sn-cart-upsell',
          itemCount: 1,
          productId: String(formData.get('product-id') || ''),
          sections: data.sections,
        })
      );
    } catch (e) {
      // Netwerkfout: de knop komt terug, de bezoeker kan opnieuw klikken.
    } finally {
      button?.removeAttribute('aria-busy');
    }
  });
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
